<template>
  <base-dialog
    :is-open="isOpen"
    max-width="550"
    @close="close"
  >
    <template #dialog-title>
      {{ label }} {{ reactivate ? 'reaktivieren' : feature ? 'bearbeiten' : 'aktivieren' }}
    </template>
    <template #dialog-text>
      <p
        v-if="help"
        class="mb-4"
      >
        {{ help }}
      </p>
      <v-alert
        v-if="reactivate"
        type="info"
        dense
        outlined
      >
        Die Organisation hatte {{ label }} bereits. Es wird mit demselben Slug reaktiviert,
        damit die bestehenden Veranstaltungen in pretix erhalten bleiben.
      </v-alert>
      <v-alert
        v-if="isMissingPaymentDevice"
        type="warning"
        dense
        outlined
      >
        Für diese Organisation (und ihre übergeordneten Organisationen) gibt es kein aktives Gerät mit gültigem
        Online-Zahlungsvertrag. Bitte zuerst im Django-Admin einen Online-Zahlungsvertrag mit Gerät anlegen.
      </v-alert>
      <v-row v-else>
        <v-col
          v-if="needsSlug && !feature"
          cols="12"
        >
          <v-text-field
            v-model="form.slug"
            label="Slug (Kennung in veranstaltung.app)"
            hint="Kann später nicht geändert werden."
            persistent-hint
            counter="50"
            outlined
            dense
            :rules="[slugRule]"
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="form.validFrom"
            :label="feature ? 'Gültig ab (optional)' : 'Gültig ab (leer: ab sofort)'"
            type="date"
            :min="feature ? null : today"
            outlined
            dense
            clearable
            hide-details
          />
        </v-col>
        <v-col
          cols="12"
          md="6"
        >
          <v-text-field
            v-model="form.lastDay"
            label="Gültig bis (optional)"
            type="date"
            :min="form.validFrom || null"
            outlined
            dense
            clearable
            hide-details
          />
        </v-col>
        <template v-if="needsPaymentDevice">
          <v-col
            v-if="!feature"
            cols="12"
          >
            <v-select
              v-model="form.paymentDevice"
              :items="paymentDevices.map(device => ({
                text: device.location ? `${device.name} (${device.location})` : device.name,
                value: device.id
              }))"
              :loading="loadingPaymentDevices"
              label="Zahlungsgerät"
              hint="Gerät mit Online-Zahlungsvertrag"
              persistent-hint
              outlined
              dense
            />
          </v-col>
          <!-- Slug and payment device are fixed once the feature exists -->
          <v-col
            v-else
            cols="12"
            class="text-body-2"
          >
            <div v-if="needsSlug">
              Slug: <strong>{{ feature.slug }}</strong>
            </div>
            <div>
              Zahlungsgerät: <strong>{{ feature.payment_device ? feature.payment_device.name : '–' }}</strong>
            </div>
          </v-col>
        </template>
        <v-col
          v-if="hasTags"
          cols="12"
        >
          <v-combobox
            v-model="form.tags"
            label="Sektionen"
            hint="Hier können Sie den Teilgruppen Namen vergeben."
            persistent-hint
            multiple
            chips
            small-chips
            deletable-chips
            outlined
            dense
            :rules="[tags => tags.every(tag => !/\s/.test(tag)) || 'Sektionen dürfen keine Leerzeichen enthalten.']"
          />
        </v-col>
      </v-row>
      <v-alert
        v-if="error"
        type="error"
        dense
        class="mt-4 mb-0"
      >
        {{ error }}
      </v-alert>
    </template>
    <template #dialog-actions>
      <v-btn
        color="primary"
        :disabled="!isValid || saving"
        :loading="saving"
        @click="save"
      >
        Speichern
      </v-btn>
      <v-btn @click="close">
        Abbrechen
      </v-btn>
    </template>
  </base-dialog>
</template>

<script>
import moment from 'moment'
import BaseDialog from '@/components/UI/BaseDialog.vue'
import { SET_SNACKBAR_MESSAGE } from '@/modules/common/store/mutation-types'
import { formatApiError } from '@/lib/api-errors'
import { UPDATE_TIME } from '@/modules/common/store/action-types'
import { lastValidDay, validNotafterFromLastDay } from '@/lib/sort-utils'

const TAGGED_COLLECTION_PLAN = 'tagged_collection_plan'
const VERANSTALTUNG_APP = 'veranstaltung_app'
const PAYMENT_DEVICE_TYPES = [VERANSTALTUNG_APP, 'kirchgeld_digital']

// Same rules as VeranstaltungAppFeature.slug in the backend
const SLUG_PATTERN = /^[a-zA-Z0-9][a-zA-Z0-9.-]*[a-zA-Z0-9]$/
const SLUG_MAX_LENGTH = 50

// "Ev. KG Groß-Umstadt" -> "ev.-kg-gross-umstadt"
const slugify = text => text
  .toLowerCase()
  .replace(/ä/g, 'ae').replace(/ö/g, 'oe').replace(/ü/g, 'ue').replace(/ß/g, 'ss')
  .normalize('NFD').replace(/[̀-ͯ]/g, '')
  .replace(/[^a-z0-9.-]+/g, '-')
  .replace(/-{2,}/g, '-')
  .replace(/^[.-]+|[.-]+$/g, '')
const suggestSlug = organization => {
  const slug = slugify(organization?.short_name || organization?.name || '')
  if (slug.length <= SLUG_MAX_LENGTH) return slug
  // Too long: cut at the last word boundary instead of in the middle of a word
  const cut = slug.slice(0, SLUG_MAX_LENGTH)
  const boundary = cut.lastIndexOf('-')
  return (boundary >= 2 ? cut.slice(0, boundary) : cut).replace(/[.-]+$/, '')
}

// Dates are edited as days: valid_from is the first valid day, "Gültig bis" the last one
// (valid_notafter is stored as the exclusive start of the following day)
const toForm = feature => ({
  validFrom: feature?.valid_from ? moment(feature.valid_from).format('YYYY-MM-DD') : '',
  lastDay: lastValidDay(feature?.valid_notafter)?.format('YYYY-MM-DD') ?? '',
  tags: [...(feature?.tags ?? [])],
  slug: '',
  paymentDevice: null
})

export default {
  name: 'FeatureEditDialog',
  components: { BaseDialog },
  props: {
    isOpen: {
      type: Boolean,
      required: true
    },
    label: {
      type: String,
      required: true
    },
    // Backend FeatureType value, needed to create a feature
    type: {
      type: String,
      default: null
    },
    organization: {
      type: Object,
      required: true
    },
    // Explanation of the feature, shown above the form
    help: {
      type: String,
      default: null
    },
    // Reopen the given (expired) feature: its end date is cleared
    reactivate: {
      type: Boolean,
      default: false
    },
    // Existing feature to edit; null creates a new one
    feature: {
      type: Object,
      default: null
    }
  },
  emits: ['close', 'saved'],
  data () {
    return {
      form: toForm(null),
      initial: toForm(null),
      paymentDevices: [],
      loadingPaymentDevices: false,
      saving: false,
      error: null
    }
  },
  computed: {
    today () {
      return moment().format('YYYY-MM-DD')
    },
    featureType () {
      return this.feature?.type ?? this.type
    },
    hasTags () {
      return this.featureType === TAGGED_COLLECTION_PLAN
    },
    needsPaymentDevice () {
      return PAYMENT_DEVICE_TYPES.includes(this.featureType)
    },
    needsSlug () {
      return this.featureType === VERANSTALTUNG_APP
    },
    isMissingPaymentDevice () {
      return !this.feature && this.needsPaymentDevice && !this.loadingPaymentDevices && !this.paymentDevices.length && !this.error
    },
    slugRule () {
      return slug => {
        if (!slug || slug.length < 2) return 'Mindestens 2 Zeichen.'
        if (slug.length > SLUG_MAX_LENGTH) return `Höchstens ${SLUG_MAX_LENGTH} Zeichen.`
        return SLUG_PATTERN.test(slug) || 'Nur Buchstaben, Zahlen, Punkte und Bindestriche; Anfang und Ende Buchstabe oder Zahl.'
      }
    },
    isValid () {
      const datesValid = !this.form.validFrom || !this.form.lastDay || this.form.lastDay >= this.form.validFrom
      const tagsValid = !this.hasTags || this.form.tags.every(tag => !/\s/.test(tag))
      const isCreatingWithDevice = !this.feature && this.needsPaymentDevice
      const deviceValid = !isCreatingWithDevice || !!this.form.paymentDevice
      const slugValid = !isCreatingWithDevice || !this.needsSlug || this.slugRule(this.form.slug) === true
      return datesValid && tagsValid && deviceValid && slugValid && !this.isMissingPaymentDevice
    }
  },
  watch: {
    isOpen (val) {
      if (!val) return
      this.error = null
      this.saving = false
      this.form = toForm(this.feature)
      this.initial = toForm(this.feature)
      if (this.reactivate) {
        this.form.lastDay = ''
      }
      this.paymentDevices = []
      if (!this.feature && this.needsPaymentDevice) {
        this.form.slug = this.needsSlug ? suggestSlug(this.organization) : ''
        this.loadPaymentDevices()
      }
    }
  },
  methods: {
    async loadPaymentDevices () {
      this.loadingPaymentDevices = true
      try {
        const response = await this.$store.getters.restApi.get('feature/payment_devices/', {
          params: { organization: this.organization.id }
        })
        this.paymentDevices = response.data
        if (this.paymentDevices.length === 1) {
          this.form.paymentDevice = this.paymentDevices[0].id
        }
      } catch (e) {
        this.error = formatApiError(e, { fallback: 'Zahlungsgeräte konnten nicht geladen werden.' })
      } finally {
        this.loadingPaymentDevices = false
      }
    },
    payload () {
      const data = {}
      // Only send what changed, so untouched timestamps keep their exact time
      if (!this.feature) {
        // A new feature starts now at the earliest: an open or past start would overlap
        // with an earlier (e.g. just ended) feature of the same type
        const start = this.form.validFrom ? moment(this.form.validFrom, 'YYYY-MM-DD').startOf('day') : null
        data.valid_from = (!start || start.isBefore(moment()) ? moment() : start).format()
      } else if (this.form.validFrom !== this.initial.validFrom) {
        data.valid_from = this.form.validFrom ? moment(this.form.validFrom, 'YYYY-MM-DD').startOf('day').format() : null
      }
      if (!this.feature || this.form.lastDay !== this.initial.lastDay) {
        data.valid_notafter = validNotafterFromLastDay(this.form.lastDay)?.format() ?? null
      }
      if (this.hasTags) {
        data.tags = this.form.tags
      }
      if (!this.feature && this.needsPaymentDevice) {
        data.payment_device = this.form.paymentDevice
        if (this.needsSlug) {
          data.slug = this.form.slug
        }
      }
      return data
    },
    async save () {
      this.saving = true
      this.error = null
      try {
        if (this.feature) {
          await this.$store.getters.restApi.patch(`feature/${this.feature.id}/`, this.payload())
        } else {
          await this.$store.getters.restApi.post('feature/', {
            organization: this.organization.url,
            type: this.type,
            ...this.payload()
          })
        }
        await this.$store.dispatch(UPDATE_TIME)
        this.$store.commit(SET_SNACKBAR_MESSAGE, {
          message: `${this.label} wurde gespeichert.`,
          color: 'success'
        })
        this.$emit('saved')
        this.close()
      } catch (e) {
        this.error = formatApiError(e)
      } finally {
        this.saving = false
      }
    },
    close () {
      this.$emit('close')
    }
  }
}
</script>
